import { useState, useEffect } from "react";
import {
  Modal,
  Input,
  Select,
  DatePicker,
  TimePicker,
  Radio,
  message,
} from "antd";
import dayjs from "dayjs";
import CourseFilterDropdown from "./CourseFilterDropdown";
const { Option } = Select;

const EditEventModal = ({ isOpen, onClose, editingEvent, onUpdate }) => {
  const [event, setEvent] = useState(null);

  useEffect(() => {
    if (editingEvent) {
      setEvent({ ...editingEvent });
    }
  }, [editingEvent]);

  const validateForm = () => {
    if (!event.topic.trim()) {
      message.error("Topic is required");
      return false;
    }
    if (!event.trainer.trim()) {
      message.error("Trainer name is required");
      return false;
    }
    if (!event.start_date) {
      message.error("Start date is required");
      return false;
    }
    if (!event.end_date) {
      message.error("End date is required");
      return false;
    }

    const today = dayjs().startOf("day");
    const start = dayjs(event.start_date);
    const end = dayjs(event.end_date);
    if (start.isBefore(today)) {
      message.error("Start date cannot be before today");
      return false;
    }
    if (end.isBefore(start)) {
      message.error("End date must be after start date");
      return false;
    }

    if (!event.timing) {
      message.error("Start and end time are required");
      return false;
    }
    const [startTimeStr, endTimeStr] = event.timing.split(" - ");
    const startTime = dayjs(startTimeStr, "hh:mm A");
    const endTime = dayjs(endTimeStr, "hh:mm A");
    if (
      !startTime.isValid() ||
      !endTime.isValid() ||
      endTime.isBefore(startTime)
    ) {
      message.error("End time must be after start time");
      return false;
    }

    if (event.type === "Virtual" && !event.link) {
      message.error("Event link is required");
      return false;
    }
    if (event.type === "ilt" && !event.location) {
      message.error("Location is required for offline event");
      return false;
    }
    if (event.type === "e-learning" && !event.course_id) {
      message.error("Please select a course");
      return false;
    }

    if (event.access_type === "Limited") {
      if (!event.min_participants || !event.max_participants) {
        message.error("Min and Max participants are required");
        return false;
      }
      if (Number(event.min_participants) > Number(event.max_participants)) {
        message.error(
          "Min participants cannot be greater than Max participants"
        );
        return false;
      }
    }

    if (event.mandatory === null || event.mandatory === undefined) {
      message.error("Mandatory field is required");
      return false;
    }

    return true;
  };

  const handleUpdate = () => {
    if (!event || !validateForm()) return;
    onUpdate(event);
  };

  if (!event) return null;

  return (
    <Modal
      title="Edit Event"
      open={isOpen}
      onCancel={onClose}
      onOk={handleUpdate}
      okText="Update"
      width={600}
    >
      <div className="event-form-container">
        <label className="form-label-first">Event Mode</label>
        <Radio.Group
          value={event.type}
          onChange={(e) => setEvent({ ...event, type: e.target.value })}
        >
          <Radio value="virtual">Virtual</Radio>
          <Radio value="ilt">ILT</Radio>
          <Radio value="e-learning">E-learning</Radio>
        </Radio.Group>

        {/* NEW MANDATORY FIELD */}
        <label className="form-label">Mandatory</label>
        <Select
          value={event.mandatory ? "Yes" : "No"} // convert boolean to string
          placeholder="Is this event mandatory?"
          className="form-select full-width"
          onChange={(val) => setEvent({ ...event, mandatory: val === "Yes" })} // convert string to boolean
        >
          <Option value="Yes">Yes</Option>
          <Option value="No">No</Option>
        </Select>

        <label className="form-label-first">Topic</label>
        <Input
          value={event.topic}
          onChange={(e) => setEvent({ ...event, topic: e.target.value })}
          className="form-input"
        />

        <label className="form-label">Trainer</label>
        <Input
          value={event.trainer}
          onChange={(e) => setEvent({ ...event, trainer: e.target.value })}
          className="form-input"
        />

        <label className="form-label">Start Date</label>
        <DatePicker
          value={event.start_date ? dayjs(event.start_date) : null}
          onChange={(_, dateStr) => setEvent({ ...event, start_date: dateStr })}
          className="form-input full-width"
          disabledDate={(current) =>
            current && current < dayjs().startOf("day")
          }
        />

        <label className="form-label">End Date</label>
        <DatePicker
          value={event.end_date ? dayjs(event.end_date) : null}
          onChange={(_, dateStr) => setEvent({ ...event, end_date: dateStr })}
          className="form-input full-width"
          disabledDate={(current) =>
            current && current < dayjs().startOf("day")
          }
        />

        <label className="form-label">Start & End Timing</label>
        <TimePicker.RangePicker
          use12Hours
          format="hh:mm A"
          className="form-input full-width"
          value={
            event.timing
              ? event.timing.split(" - ").map((t) => dayjs(t, "hh:mm A"))
              : []
          }
          onChange={(value) => {
            const timing = value
              ? value.map((v) => v.format("hh:mm A")).join(" - ")
              : "";
            setEvent({ ...event, timing });
          }}
        />

        {event.type === "virtual" ? (
          <>
            <label className="form-label">Event Link</label>
            <Input
              value={event.link}
              onChange={(e) => setEvent({ ...event, link: e.target.value })}
              className="form-input"
            />
          </>
        ) : event.type === "ilt" ? (
          <>
            <label className="form-label">Location</label>
            <Select
              value={event.location || undefined}
              placeholder="Select location"
              className="form-select full-width"
              onChange={(val) => setEvent({ ...event, location: val })}
            >
              <Option value="Room A">Room A</Option>
              <Option value="Room B">Room B</Option>
            </Select>
          </>
        ) : event.type === "e-learning" ? (
          <>
            <>
              <label className="form-label">Select Course</label>
              <CourseFilterDropdown
                value={{
                  course_id: event.course_id,
                  course_name: event.course_name,
                }}
                onChange={(courseData) =>
                  setEvent({
                    ...event,
                    course_id: courseData?.course_id,
                    course_name: courseData?.course_name,
                  })
                }
                placeholder="Choose a course"
              />
            </>
          </>
        ) : null}

        <label className="form-label">Event Type</label>
        <Select
          value={event.mandatory ? "Open" : event.access_type}
          disabled={event.mandatory} // 🔥 mandatory → locked
          className="form-select full-width"
          onChange={(val) => setEvent({ ...event, access_type: val })}
        >
          <Option value="Open">Open</Option>
          {!event.mandatory && <Option value="Limited">Limited</Option>}
        </Select>

        {!event.mandatory && event.access_type === "Limited" && (
          <>
            <label className="form-label">Min Participants</label>
            <Input
              value={event.min_participants}
              type="number"
              onChange={(e) =>
                setEvent({ ...event, min_participants: e.target.value })
              }
              className="form-input"
            />

            <label className="form-label">Max Participants</label>
            <Input
              value={event.max_participants}
              type="number"
              onChange={(e) =>
                setEvent({ ...event, max_participants: e.target.value })
              }
              className="form-input"
            />
          </>
        )}

        <label className="form-label">Status</label>
        <Select
          value={event.status}
          onChange={(val) => setEvent({ ...event, status: val })}
          className="form-select full-width"
        >
          <Option value="Open">Open</Option>
          <Option value="Closed">Closed</Option>
        </Select>
      </div>
    </Modal>
  );
};

export default EditEventModal;
